import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Eye, EyeOff, Loader2, KeyRound } from "lucide-react";
import { getErrorMessage } from "@/lib/errorUtils";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirm_password: ""
  });

  const token = searchParams.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Le password non coincidono");
      return;
    }

    if (!token) {
      toast.error("Token non valido");
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, formData.password);
      setSuccess(true);
      toast.success("Password reimpostata con successo!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nel reset della password"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
        </div>
        <motion.div
          className="glass-card p-8 max-w-md w-full text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-semibold mb-4">Link non valido</h1>
          <p className="text-muted-foreground mb-6">
            Il link per reimpostare la password non è valido o è scaduto.
          </p>
          <Link to="/">
            <Button className="rounded-full btn-gold">
              Torna alla Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-success-page">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
        </div>
        <motion.div
          className="glass-card p-8 max-w-md w-full text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 mx-auto rounded-full bg-[#fcb900]/20 flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-[#fcb900]" />
          </div>
          <h1 className="text-2xl font-semibold mb-2">Password Reimpostata!</h1>
          <p className="text-muted-foreground mb-6">
            La tua password è stata reimpostata con successo. Ora puoi effettuare il login con la nuova password.
          </p>
          <Link to="/">
            <Button className="rounded-full btn-gold" data-testid="go-to-login-btn">
              Vai al Login
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
      </div>
      <motion.div
        className="glass-card p-8 max-w-md w-full relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#fcb900]/20 flex items-center justify-center mb-4">
            <KeyRound className="w-8 h-8 text-[#fcb900]" />
          </div>
          <h1 className="text-2xl font-semibold mb-2">Reimposta Password</h1>
          <p className="text-muted-foreground">
            Inserisci la tua nuova password
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="password">Nuova Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-black/20 border-white/10 h-11 pr-10"
                placeholder="Min 8 caratteri, 1 maiuscola, 1 speciale"
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Conferma Password</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="bg-black/20 border-white/10 h-11 pr-10"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 rounded-full btn-gold"
            disabled={isLoading}
            data-testid="submit-reset-btn"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Reimposta Password
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
